import { ArrowRight } from "lucide-react";
import { Eyebrow, IMAGES, Reveal } from "./shared";

const STEPS = [
    {
        title: "Discover",
        copy: "We listen first — your roles, standards, locations and timelines.",
    },
    {
        title: "Source",
        copy: "We reach qualified candidates across our networks in Nigeria and partner markets.",
    },
    {
        title: "Screen",
        copy: "Credentials, experience and relocation readiness are carefully reviewed.",
    },
    {
        title: "Select",
        copy: "You meet a considered shortlist and make the final hiring decision.",
    },
    {
        title: "Mobilize",
        copy: "We coordinate documentation, travel preparation and relocation logistics.",
    },
    {
        title: "Deploy",
        copy: "Candidates arrive ready, with structured follow-through from our team.",
    },
];

export const Recruitment = () => (
    <section id="recruitment" data-testid="recruitment-section" className="bg-ivory py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
                <Reveal className="lg:col-span-7">
                    <Eyebrow>Recruitment — Our Core Business</Eyebrow>
                    <h2 className="mt-5 max-w-xl font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Your Workforce, Without Borders
                    </h2>
                </Reveal>
                <Reveal delay={0.1} className="lg:col-span-5">
                    <p className="max-w-md text-base leading-relaxed text-ink/65">
                        We help organizations identify and connect with talent for international workforce
                        requirements across major sectors — through one structured journey.
                    </p>
                    <a
                        href="/recruitment"
                        data-testid="recruitment-start-request-btn"
                        className="mt-8 inline-flex items-center gap-2 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-ivory transition-colors duration-300 hover:bg-forest-deep"
                    >
                        Start a Recruitment Request
                        <ArrowRight size={16} strokeWidth={1.5} />
                    </a>
                </Reveal>
            </div>

            <Reveal className="mt-14">
                <div className="group overflow-hidden">
                    <img
                        src={IMAGES.recruitment}
                        alt="International professionals collaborating on a workforce project"
                        loading="lazy"
                        className="aspect-[16/9] w-full object-cover object-[50%_30%] transition-transform duration-[1200ms] ease-out group-hover:scale-105 lg:aspect-[16/7]"
                    />
                </div>
            </Reveal>

            <Reveal className="mt-16 lg:mt-24">
                <div className="relative">
                    <div
                        className="absolute inset-x-0 top-[5px] hidden h-px bg-ink/15 xl:block"
                        aria-hidden="true"
                    />
                    <ol className="grid gap-x-8 gap-y-12 sm:grid-cols-2 xl:grid-cols-6 xl:gap-x-10">
                        {STEPS.map((step, i) => (
                            <li
                                key={step.title}
                                data-testid={`recruitment-step-${i + 1}`}
                                className="relative border-t border-ink/15 pt-6 xl:border-t-0 xl:pt-9"
                            >
                                <span
                                    className="absolute left-0 top-0 hidden h-2.5 w-2.5 rotate-45 bg-champagne xl:block"
                                    aria-hidden="true"
                                />
                                <span className="font-serif text-4xl font-medium text-forest/30">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <h3 className="mt-4 text-base font-semibold uppercase tracking-[0.08em] text-ink">
                                    {step.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-ink/60">{step.copy}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </Reveal>
        </div>
    </section>
);
